import { useIntl } from 'react-intl';

import { WLIDS } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import { widthRequiresNonBase } from 'Util/_HumanWritten/WidthUtils';
import { Flex } from 'Views/Components/Flex/Flex';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { VehicleTabActionCreators, wizardDataVehicleTabReducer } from './Reducers/vehicleTabReducer';
import VehicleWidthInput from './VehicleWidthInput';

import { useVehicleTabContext } from './VehicleTabContext';

interface VehicleWidthDetailsProps {
	checkboxLabel?: string;
	checkboxClassName?: string;
}

export default function VehicleWidthDetails({ checkboxLabel, checkboxClassName }: VehicleWidthDetailsProps = {}) {
	const { cargoTypes, towOnTypes, prices } = useVehicleTabContext();
	const intl = useIntl();
	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	if (wizardData.innerWizardData.type !== 'vehicle') {
		return <></>;
	}

	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;

	const onChecked = (checked: boolean) => {
		const newData = wizardDataVehicleTabReducer(wizardData, VehicleTabActionCreators.hasWidthDetails({ checked }));
		if (newData) {
			setWizardData(newData);
		}

		if (!checked) {
			const newErrors = { ...typedErrors };
			delete newErrors.vehicleWidthId;
			setInnerWizardErrors(newErrors);
		}
	};

	// Disable the toggle when a required width must stay applied. The vehicle requires a width when
	// its cargo type's minimum width exceeds the base width. When the vehicle and trailer widths are
	// linked they share one width, so the checkbox must also stay ticked when the TRAILER's tow-on
	// type requires a width — otherwise unticking here would strip the required width off both.
	const isDisabled = () => {
		if (!isVehicleBookingWizardData(wizardData.innerWizardData)) {
			return false;
		}

		const { cargoTypeId, trailerTypeId } = wizardData.innerWizardData;
		const vehicleRequiresWidth = widthRequiresNonBase(cargoTypes.find(v => v.id === cargoTypeId)?.minimumWidthId);
		const trailerRequiresWidth =
			appConfigStore.vehicleAndTrailerWidthLinked &&
			widthRequiresNonBase(towOnTypes.find(v => v.id === trailerTypeId)?.minimumWidthId);

		return vehicleRequiresWidth || trailerRequiresWidth;
	};

	const onChange = (vehicleWidthId: string) => {
		const newData = wizardDataVehicleTabReducer(
			wizardData,
			VehicleTabActionCreators.changeWidth({ widthId: vehicleWidthId }),
		);
		if (newData) {
			setWizardData(newData);
		}

		if (vehicleWidthId) {
			const newErrors = { ...typedErrors };
			delete newErrors.vehicleWidthId;
			setInnerWizardErrors(newErrors);
		}
	};

	const defaultLabel = intl.formatMessage({
		id: WLIDS.width.checkboxLabel,
		defaultMessage: 'My vehicle is narrower/wider than standard',
	});

	// When the vehicle and trailer widths are linked this single picker drives both, so it must also
	// disable widths below the trailer's minimum (otherwise a wider-than-standard trailer could be
	// given a too-narrow width).
	const trailerTypeId = isVehicleBookingWizardData(wizardData.innerWizardData)
		? wizardData.innerWizardData.trailerTypeId
		: undefined;
	const additionalMinimumWidthId = appConfigStore.vehicleAndTrailerWidthLinked
		? towOnTypes.find(v => v.id === trailerTypeId)?.minimumWidthId
		: undefined;

	return (
		<Flex direction="col" flex={1} gap="xs">
			<CheckboxInput
				className={`vehicle-width-checkbox${checkboxClassName ? ` ${checkboxClassName}` : ''}`}
				label={checkboxLabel ?? defaultLabel}
				disabled={isDisabled()}
				checked={!!wizardData.innerWizardData.showWidthDetails}
				onChange={e => onChecked(e.target.checked)}
			/>
			{wizardData.innerWizardData.showWidthDetails && (
				<VehicleWidthInput
					cargoTypes={cargoTypes}
					cargoTypeId={wizardData.innerWizardData.cargoTypeId}
					vehicleWidthId={wizardData.innerWizardData.vehicleWidthId}
					onChange={onChange}
					prices={prices}
					isRequired
					errors={typedErrors?.vehicleWidthId}
					additionalMinimumWidthId={additionalMinimumWidthId}
				/>
			)}
		</Flex>
	);
}
